import React from 'react';
import {
    Button,
    ButtonVariants,
    ButtonColors,
    ButtonFontWeights,
    Text,
    TextAs,
    TextSizes,
    Tooltip,
} from '@clockout-dev/design-elements';
import { SyncOutlined } from '@ant-design/icons';
import SectionCard from '../../../common/sectionCard/SectionCard';
import UserCardRow from './UserCardRow';
import UserStatePill from '../../../common/userStatePill/UserStatePill';
import { describeSyncReport } from '../../../reports/elements/syncStatus.constants';
import { SYNC_DETAIL_STATUS, SYNC_DETAIL_ERROR } from '../../../reports/reports.constants';
import styles from './styles/payroll-sync-status-card.module.scss';

/**
 * "Payroll Sync Status" card (Figma 5747:1772) on the user page. Renders the
 * rollup pill and one row per dimension from the SAME mapping the Reports
 * table and detail panel use (`syncStatus.constants`), so a state reads the
 * same on every screen; every pill carries a tooltip with its meaning and an
 * accessible name. `detail` is the same `{ status, report, error }` shape the
 * Reports side panel uses, so the card shows an explicit error with Retry
 * instead of an indefinite placeholder.
 *
 * @param {Object} props
 * @param {import('../../../reports/reports.constants').SyncDetail | null} props.detail
 * @param {() => void} [props.onRetry]
 * @param {() => void} props.onMarkComplete
 * @param {() => void} props.onDeleteLast
 * @param {boolean} [props.markCompleteLoading]
 * @param {boolean} [props.deleteLastLoading]
 * @param {boolean} [props.canDeleteSync] Gates "Delete Last Sync".
 * @param {boolean} [props.renderShell] When false, renders the rows and
 *   actions without the `SectionCard` wrapper, under a plain heading, so the
 *   card can sit inside a payroll-connection panel without a second card surface.
 * @param {boolean} [props.canMarkSyncComplete] Gates "Mark Sync Complete".
 *   Two flags, not one: the delete is unrecoverable and the mark-complete is
 *   not, so they are separate scopes server-side and a role may hold either.
 */
function PayrollSyncStatusCard({
    detail = null,
    onRetry,
    onMarkComplete,
    onDeleteLast,
    markCompleteLoading,
    deleteLastLoading,
    canDeleteSync = true,
    canMarkSyncComplete = true,
    renderShell = true,
}) {
    const loading = detail?.status === SYNC_DETAIL_STATUS.LOADING;
    const error = detail?.status === SYNC_DETAIL_STATUS.ERROR ? (detail.error || SYNC_DETAIL_ERROR.LOAD_FAILED) : null;
    // While reloading, the controller keeps the previous report on `detail`,
    // so the pill and rows stay put instead of flashing "Unavailable".
    const report = detail?.status === SYNC_DETAIL_STATUS.READY || loading ? detail?.report ?? null : null;
    const described = describeSyncReport(report);
    // First load (no report yet): a distinct loading pill, never the
    // "could not be loaded" one.
    const rollup = loading && !report
        ? { label: 'Loading', tone: 'neutral', description: 'Loading payroll sync status…' }
        : described.rollup;
    const { dimensions, inconsistency } = described;
    const showRows = !!report;

    const content = (
        <>
            {!renderShell ? (
                // Same heading level and size the connection panel's own
                // "Last events" list uses, so the nested card reads as a
                // section of that panel rather than as loose rows.
                <Text as={TextAs.H4} size={TextSizes.SMALL}>
                    Payroll Sync Status
                </Text>
            ) : null}
            <div className={styles.rows}>
                <UserCardRow
                    label="Overall"
                    value={(
                        <Tooltip title={inconsistency ? `${rollup.description} ${inconsistency}` : rollup.description} placement="left">
                            <span role="status" aria-label={`Sync status: ${rollup.label}`}>
                                <UserStatePill tone={rollup.tone} label={rollup.label} />
                            </span>
                        </Tooltip>
                    )}
                />
                {inconsistency ? (
                    <Text as={TextAs.P} size={TextSizes.EXTRA_SMALL} className={styles.note}>
                        {inconsistency}
                    </Text>
                ) : null}

                {loading ? (
                    <Text as={TextAs.P} size={TextSizes.SMALL} className={styles.note} role="status" aria-live="polite">
                        Loading payroll dimensions…
                    </Text>
                ) : null}

                {error ? (
                    <div className={styles.errorRow} role="alert">
                        <Text as={TextAs.SPAN} size={TextSizes.SMALL} className={styles.errorText}>
                            {error === SYNC_DETAIL_ERROR.TIMEOUT ? 'Payroll dimensions took too long to load.' : "Couldn't load payroll dimensions."}
                        </Text>
                        <Button
                            variant={ButtonVariants.DEFAULT}
                            color={ButtonColors.SECONDARY}
                            onClick={onRetry}
                            className={styles.retryBtn}
                        >
                            Retry
                        </Button>
                    </div>
                ) : null}

                {showRows ? dimensions.map(({ key, label, state }) => (
                    <UserCardRow
                        key={key}
                        label={label}
                        value={(
                            <Tooltip title={state.description} placement="left">
                                {!state.applicable ? (
                                    <Text
                                        as={TextAs.SPAN}
                                        size={TextSizes.SMALL}
                                        className={styles.dimValueNa}
                                        aria-label={`${label}: ${state.label}`}
                                    >
                                        {state.label}
                                    </Text>
                                ) : (
                                    <span role="status" aria-label={`${label}: ${state.label}`}>
                                        <UserStatePill tone={state.tone} label={state.label} />
                                    </span>
                                )}
                            </Tooltip>
                        )}
                    />
                )) : null}
            </div>

            {canMarkSyncComplete || canDeleteSync ? (
                <div className={styles.footer}>
                    {canMarkSyncComplete ? (
                        <Button
                            variant={ButtonVariants.DEFAULT}
                            color={ButtonColors.PRIMARY}
                            fontWeight={ButtonFontWeights.BOLD}
                            onClick={onMarkComplete}
                            disabled={!!markCompleteLoading}
                            className={`${styles.footerBtn} ${styles.footerBtnPrimary}`}
                        >
                            Mark Sync Complete
                        </Button>
                    ) : null}
                    {canDeleteSync ? (
                        <Button
                            variant={ButtonVariants.DEFAULT}
                            color={ButtonColors.LIGHT}
                            fontWeight={ButtonFontWeights.BOLD}
                            onClick={onDeleteLast}
                            disabled={!!deleteLastLoading}
                            className={`${styles.footerBtn} ${styles.footerBtnDanger}`}
                        >
                            Delete Last Sync
                        </Button>
                    ) : null}
                </div>
            ) : null}
        </>
    );

    if (!renderShell) {
        return content;
    }

    return (
        <SectionCard icon={<SyncOutlined />} title="Payroll Sync Status">
            {content}
        </SectionCard>
    );
}

export default PayrollSyncStatusCard;
